import React, { useId, useState } from "react";
import styled, { DefaultTheme, useTheme } from "styled-components";
import { ChevronDown } from "lucide-react";
import {
  BacktestingScoreBreakdown,
  LanguageNameSpaces,
  MarketplaceSubGroup,
} from "@shared/types";
import useScore from "@shared/features/newMarketplace/hooks/useScore";
import { ScoreSection } from "@shared/features/newMarketplace/types";
import {
  SCORE_HELPER_PROPS,
  SCORE_SECTION_TO_KEYS,
} from "@shared/features/newMarketplace/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { SCORE_BREAKDOWN_ID, SCORE_METRIC_ID, SCORE_SECTION_ID } from "testIds";
import { ScoreRing } from "features/newMarketplace/NewLook/NewLookScoreBadge";

interface Props {
  group?: MarketplaceSubGroup;
  loading?: boolean;
  hideTitle?: boolean;
}

export const SCORE_SECTION_LABELS = {
  [ScoreSection.PERFORMANCE]: "score.sections.performance",
  [ScoreSection.RISK]: "score.sections.risk",
  [ScoreSection.SERVICE]: "score.sections.service",
} as const;

/** Each score category keeps one colour: its arc, its ring and its bars. */
export const getScoreSectionColor = (
  theme: DefaultTheme,
  section: ScoreSection
) => {
  if (section === ScoreSection.RISK) return theme.colors.yellow;
  if (section === ScoreSection.SERVICE) return theme.colors.pieChartColors[1];
  return theme.colors.primaryBlue;
};

export const getScoreVerdict = (ratio: number) =>
  ratio >= 0.85
    ? { key: "score.verdict.excellent" as const, tone: "up" as const }
    : ratio >= 0.7
    ? { key: "score.verdict.strong" as const, tone: "accent" as const }
    : ratio >= 0.5
    ? { key: "score.verdict.fair" as const, tone: "stop" as const }
    : { key: "score.verdict.weak" as const, tone: "down" as const };

/**
 * The score's total and its maximum. The total is the group's own score, the
 * figure its badge and ring show (the categories' rounded parts can add up a
 * point off); without one, the categories' sum.
 */
export const getScoreTotals = (
  group: MarketplaceSubGroup | undefined,
  score: ReturnType<typeof useScore>
) => {
  const total = (key: "weighted_score" | "weight") =>
    Math.round(
      score.sections.reduce(
        (sum, section) => sum + score.getSumSectionByKey(section, key),
        0
      )
    );
  const ownScore = group?.backtesting_score;
  return {
    totalScore:
      typeof ownScore === "number" && Number.isFinite(ownScore)
        ? Math.round(ownScore * 100)
        : total("weighted_score"),
    totalWeight: total("weight"),
  };
};

/** Metrics shown before "Show more" in each category. */
const VISIBLE_METRICS = 2;

const clampPercent = (value?: number) =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? value || 0 : 0));

/**
 * The channel's score at full detail (the group drawer's statistics panel):
 * the total as a semicircle gauge with one arc per category, then each
 * category with its leading metrics open and the rest a click away.
 */
const NewLookScoreDetails = ({ group, loading, hideTitle }: Props) => {
  const theme = useTheme();
  const score = useScore({ group });
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const [showAll, setShowAll] = useState<Record<ScoreSection, boolean>>({
    [ScoreSection.PERFORMANCE]: false,
    [ScoreSection.RISK]: false,
    [ScoreSection.SERVICE]: false,
  });

  if (loading) {
    return (
      <Content aria-busy={true}>
        <Skeleton width={120} height={22} />
        <Skeleton
          width={240}
          height={124}
          radius={12}
          style={{ margin: "0 auto" }}
        />
        <Skeleton width="100%" height={240} radius={12} />
      </Content>
    );
  }

  const { totalScore, totalWeight } = getScoreTotals(group, score);
  const verdict = getScoreVerdict(totalScore / Math.max(1, totalWeight));

  const metric = (
    key: keyof BacktestingScoreBreakdown,
    section: ScoreSection
  ) => {
    const data = score.getProgressBarData(key, section);
    const label = (
      <MetricLabel>
        {data.title}
        {data.helperProps && <HelperTooltip {...data.helperProps} />}
      </MetricLabel>
    );
    const points = (
      <Points>
        <strong>{data.weightedScore}</strong> / {data.weight}
      </Points>
    );
    return (
      <Metric key={key} data-testid={SCORE_METRIC_ID}>
        {data.isCollapsible ? (
          <Timeframes>
            <summary>
              <MetricRow>
                <MetricName>
                  {label}
                  <ChevronDown
                    size={14}
                    strokeWidth={1.75}
                    aria-label={t("score.timeframes")}
                  />
                </MetricName>
                {points}
              </MetricRow>
            </summary>
            <TimeframeRows>
              {score
                .getTimeframeScoreData(key, section)
                .map(({ timeframe, value, weightedScore, weight }) => (
                  <MetricRow key={timeframe}>
                    <TimeframeValue>
                      {t("score.last_days", {
                        timeframe,
                        value: score.getFormattedValue(key, value),
                      })}
                    </TimeframeValue>
                    <Points>
                      {weightedScore} / {weight}
                    </Points>
                  </MetricRow>
                ))}
            </TimeframeRows>
          </Timeframes>
        ) : (
          <MetricRow>
            <MetricName>
              {label}
              <MetricValue>{data.value}</MetricValue>
            </MetricName>
            {points}
          </MetricRow>
        )}
        <Bar>
          <span
            style={{
              width: `${clampPercent(data.progressPercent)}%`,
              background: getScoreSectionColor(theme, section),
            }}
          />
        </Bar>
      </Metric>
    );
  };

  return (
    <Content data-testid={SCORE_BREAKDOWN_ID}>
      {!hideTitle && (
        <Heading>
          {t("score.title")}
          <HelperTooltip
            {...SCORE_HELPER_PROPS}
            title={t(SCORE_HELPER_PROPS.title)}
            description={t(SCORE_HELPER_PROPS.description)}
          />
        </Heading>
      )}
      <Overview>
        <ScoreGauge
          arcs={score.sections.map((section) => {
            const data = score.getSectionRowData(section);
            return {
              key: section,
              percent: clampPercent(data.progressPercent),
              color: getScoreSectionColor(theme, section),
            };
          })}
        >
          <GaugeLabel>{tCommon("labels.total")}</GaugeLabel>
          <GaugeTotal>
            <strong>{totalScore}</strong> / {totalWeight}
          </GaugeTotal>
        </ScoreGauge>
        <VerdictLine>
          {group?.name && <GroupName>{group.name}</GroupName>}
          <Verdict $tone={verdict.tone}>{t(verdict.key)}</Verdict>
        </VerdictLine>
      </Overview>
      <Sections>
        {score.sections.map((section) => {
          const data = score.getSectionRowData(section);
          const color = getScoreSectionColor(theme, section);
          const keys = SCORE_SECTION_TO_KEYS[section];
          const extra = keys.slice(VISIBLE_METRICS);
          const expanded = showAll[section];
          return (
            <Section
              key={`${group?.group_id}-${section}`}
              data-testid={SCORE_SECTION_ID}
            >
              <SectionHeader>
                <ScoreRing
                  score={Math.max(0.001, (data.progressPercent || 0) / 100)}
                  label={String(data.weightedScore)}
                  size={40}
                  color={color}
                  decorative={true}
                />
                <SectionName>{t(SCORE_SECTION_LABELS[section])}</SectionName>
                <Points $large={true}>
                  <strong>{data.weightedScore}</strong> / {data.weight}
                </Points>
              </SectionHeader>
              <Metrics>
                {keys
                  .slice(0, VISIBLE_METRICS)
                  .map((key) => metric(key, section))}
              </Metrics>
              {extra.length > 0 && (
                <>
                  <ExpandCollapse expanded={expanded}>
                    <Metrics>
                      {extra.map((key) => metric(key, section))}
                    </Metrics>
                  </ExpandCollapse>
                  <MoreToggle
                    type="button"
                    aria-expanded={expanded}
                    $expanded={expanded}
                    onClick={() =>
                      setShowAll((prev) => ({
                        ...prev,
                        [section]: !prev[section],
                      }))
                    }
                  >
                    {expanded
                      ? tCommon("filters.common.show_less")
                      : tCommon("filters.common.show_more")}
                    <ChevronDown
                      size={14}
                      strokeWidth={1.75}
                      aria-hidden={true}
                    />
                  </MoreToggle>
                </>
              )}
            </Section>
          );
        })}
      </Sections>
    </Content>
  );
};

export default NewLookScoreDetails;

interface ScoreGaugeProps {
  arcs: { key: string; percent: number; color: string }[];
  children?: React.ReactNode;
}

const GAUGE_STROKE = 8;
const GAUGE_GAP = 6;
const GAUGE_OUTER_RADIUS = 116;

/** Concentric half rings, outermost first, with the total in the middle. */
const ScoreGauge = ({ arcs, children }: ScoreGaugeProps) => {
  const gradientId = `score-gauge-${useId().replace(/[^\w-]/g, "")}`;
  const width = GAUGE_OUTER_RADIUS * 2 + GAUGE_STROKE;
  const cx = width / 2;
  const cy = GAUGE_OUTER_RADIUS + GAUGE_STROKE / 2;
  const height = cy + GAUGE_STROKE / 2;
  return (
    <Gauge style={{ width, height }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden={true}
      >
        {arcs.map((arc, index) => {
          const r = GAUGE_OUTER_RADIUS - index * (GAUGE_STROKE + GAUGE_GAP);
          const d = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
          return (
            <g key={arc.key}>
              <path
                className="track"
                d={d}
                fill="none"
                strokeWidth={GAUGE_STROKE}
                strokeLinecap="round"
              />
              {/* A mild gradient: the arc starts a little lighter and
                  reaches its full colour at the end. */}
              <defs>
                <linearGradient
                  id={`${gradientId}-${index}`}
                  gradientUnits="userSpaceOnUse"
                  x1={cx - r}
                  x2={cx + r}
                  y1={cy}
                  y2={cy}
                >
                  <stop offset="0" stopColor={arc.color} stopOpacity={0.55} />
                  <stop offset="1" stopColor={arc.color} stopOpacity={1} />
                </linearGradient>
              </defs>
              {arc.percent > 0 && (
                <path
                  d={d}
                  fill="none"
                  stroke={`url(#${gradientId}-${index})`}
                  strokeWidth={GAUGE_STROKE}
                  strokeLinecap="round"
                  pathLength={100}
                  strokeDasharray={`${arc.percent} 100`}
                />
              )}
            </g>
          );
        })}
      </svg>
      <GaugeCenter>{children}</GaugeCenter>
    </Gauge>
  );
};

const Content = styled.div`
  display: grid;
  gap: 16px;
  min-width: 0;
  text-align: left;
`;
const Heading = styled.h3`
  display: flex;
  align-items: center;
  margin: 0;
  ${newLookType.panelTitle};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Overview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 4px;
`;
const Gauge = styled.div`
  position: relative;
  flex: none;
  max-width: 100%;
  svg {
    display: block;
    max-width: 100%;
    height: auto;
  }
  .track {
    stroke: ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const GaugeCenter = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  white-space: nowrap;
`;
const GaugeLabel = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const GaugeTotal = styled.span`
  font-size: 14px;
  line-height: 34px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  strong {
    font-size: 30px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
`;
const VerdictLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  text-align: center;
`;
const GroupName = styled.span`
  ${newLookType.body};
  font-size: 13px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  overflow-wrap: anywhere;
`;
const Verdict = styled.span<{ $tone: "up" | "accent" | "stop" | "down" }>`
  padding: 1px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme)[p.$tone]};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Sections = styled.div`
  display: grid;
`;
const Section = styled.div`
  padding: 16px 0;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  &:last-child {
    padding-bottom: 0;
  }
`;
const SectionHeader = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;
const SectionName = styled.span`
  ${newLookType.panelTitle};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  overflow-wrap: anywhere;
`;
const Points = styled.span<{ $large?: boolean }>`
  flex: none;
  white-space: nowrap;
  font-size: ${(p) => (p.$large ? 13 : 12)}px;
  line-height: 18px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  strong {
    font-size: ${(p) => (p.$large ? 15 : 13.5)}px;
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
`;
const Metrics = styled.div`
  display: grid;
`;
const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
`;
const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 20px;
`;
const MetricName = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 8px;
  min-width: 0;
  > svg {
    flex: none;
    margin-left: -4px;
    color: ${(p) => getNewLookTokens(p.theme).muted};
    transition: transform 150ms;
  }
`;
const MetricLabel = styled.span`
  display: inline-flex;
  align-items: center;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const MetricValue = styled.span`
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Bar = styled.div`
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  background: ${(p) => getNewLookTokens(p.theme).hairline};
  span {
    display: block;
    height: 100%;
    border-radius: inherit;
    transition: width 300ms;
  }
`;
const Timeframes = styled.details`
  > summary {
    list-style: none;
    cursor: pointer;
    border-radius: 4px;
    &::-webkit-details-marker {
      display: none;
    }
    &:hover ${MetricLabel} {
      color: ${(p) => getNewLookTokens(p.theme).ink};
    }
    &:focus-visible {
      outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
      outline-offset: 2px;
    }
  }
  &[open] > summary ${MetricName} > svg {
    transform: rotate(180deg);
  }
`;
const TimeframeRows = styled.div`
  display: grid;
  gap: 6px;
  padding: 8px 10px;
  margin-top: 6px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
`;
const TimeframeValue = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const MoreToggle = styled.button<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 2px 0;
  border: 0;
  background: none;
  font-family: inherit;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  cursor: pointer;
  transition: color 120ms;
  svg {
    transition: transform 150ms;
    transform: rotate(${(p) => (p.$expanded ? 180 : 0)}deg);
  }
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).accent};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;
